import { getByText, render, screen, waitFor } from '@testing-library/preact';
import userEvent from '@testing-library/user-event';
import { ComponentProps, createRef } from 'preact/compat';
import { expect } from 'chai';
import { BarChart } from '../';
import { setupUseTextDimensionsDependenciesMock } from '#../test/mocks/mockUseTextDimensionsDependencies';
import { matchTranslationBundle } from '#utils/UNSAFE_matchTranslationBundle';
import { RootEnvironment, RootEnvironmentProvider } from '#UNSAFE_Environment';
import { gdpData } from '#UNSAFE_BarChart/__docs__/data/gdpData';
import {
  checkTooltipText,
  getEventCoords,
  mockMouseEventPageXYOffsetXY,
  queryAllBy,
  queryById,
  restoreMouseEventPageXYOffsetXY
} from '#utils/PRIVATE_visTestUtils';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { MenuItem } from '#UNSAFE_Menu';
import { getDndCallbacks, testDnd } from '#utils/PRIVATE_visTestUtils/dndUtils';
import {
  getTestContextMenuForNoData,
  getTestContextMenuViaKeyboard,
  getTestContextMenuViaMouse
} from '#utils/PRIVATE_visTestUtils/contextMenuUtils';
import { RatingGauge } from '#UNSAFE_RatingGauge';
import {
  checkBorderColor,
  checkNoBorder,
  checkRatingGaugeCustomContent
} from '#utils/PRIVATE_visTestUtils/domUtil';
import { ReferenceObjectData } from '#utils/UNSAFE_visTypes/chart/refObj.types';
import { SizeHandle } from '#utils/UNSAFE_visTypes/chart';

const IS_CHROME_HEADLESS = navigator?.userAgent?.includes('HeadlessChrome');
const TOOLTIP_TEST_TIMEOUT = 5000;

type BarChartProps = ComponentProps<typeof BarChart>;

const series = [
  {
    name: 'Series 1',
    id: 'Series 1',
    items: [
      {
        id: '1',
        value: 1
      },
      {
        id: '2',
        value: 2
      }
    ]
  }
];
const seriesWithZeroData = [
  ...series,
  {
    name: 'Series 2',
    id: 'Series 2',
    items: [
      { id: '3', value: 0 },
      { id: '4', value: 5 }
    ]
  },
  {
    id: 'Series 3',
    name: 'Series 3',
    items: [
      {
        value: -42,
        id: '5'
      },
      {
        value: 0,
        id: '6'
      }
    ]
  },
  {
    id: 'Series 4',
    name: 'Series 4',
    items: [
      {
        value: -4200,
        id: '7'
      },
      {
        value: -1287336233.9286213,
        id: '8'
      }
    ]
  }
];

const setupBarChart = async (
  options?: Partial<BarChartProps>,
  testOptions: { isPreferredSizeTest: boolean; returnRef?: boolean } = {
    isPreferredSizeTest: false
  }
) => {
  const defaultProps = {
    width: '500px',
    height: '500px',
    series,
    groups: [{ id: 'Group1' }, { id: 'GroupB' }],
    getDataItem: (i: number, j: number) => series[i].items[j],
    'aria-label': 'ariaText'
  } as BarChartProps;
  const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
  const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);
  const env: Partial<RootEnvironment> = {
    translations: { '@oracle/oraclejet-preact': translations }
  };
  const barChartProps = { ...defaultProps, ...options };
  const barChart = <BarChart {...barChartProps}></BarChart>;
  const chartRef = testOptions.returnRef ? createRef<SizeHandle>() : undefined;
  const view = render(
    <RootEnvironmentProvider environment={env}>
      {testOptions.returnRef ? <BarChart {...barChartProps} ref={chartRef as any} /> : barChart}
    </RootEnvironmentProvider>
  );
  await waitFor(
    () => {
      const chart = screen.getByRole('application');
      expect(chart).to.be.not.null;
    },
    {
      timeout: 5000
    }
  );

  return { ...view, chartRef };
};

describe('Test BarChart component', () => {
  beforeEach(() => {
    setupUseTextDimensionsDependenciesMock(window);
  });

  it('render', async function () {
    await setupBarChart();
    const ariaRole = screen.getByRole('application');
    expect(ariaRole).not.null;
  });

  const clientHints = getClientHints();
  if (typeof jest === 'undefined') {
    if (clientHints.browser === 'chrome') {
      mocha.timeout(TOOLTIP_TEST_TIMEOUT);

      if (!IS_CHROME_HEADLESS) {
        it('handles text rotation and truncation', async function () {
          const items = gdpData.map((i, index) => ({
            id: index,
            value: i.gdp
          }));

          await setupBarChart({
            width: '1000px',
            height: '300px',
            series: [
              {
                id: 0,
                name: 'GDP',
                items
              }
            ],
            getDataItem: (_, gIndex) => items[gIndex],
            groups: gdpData.map((i) => ({
              id: i.country
            }))
          });

          // TODO:JET-65258
          // workaround for test issue JET-65258
          // some dom are missing from chart if we do not add
          // this await userEvent.tab() and hence tests to check
          // dom will fail; maybe it's a delay issue.
          await userEvent.tab();
          await userEvent.tab();
          const chart = screen.getByRole('application');
          const queryAllByDataObject = queryAllBy('data-oj-private-text');
          const text = queryAllByDataObject(chart, 'United Arab Emirates');
          expect(text[0].textContent).equals('United Arab …');
          await userEvent.hover(text[0]);
          await waitFor(() => checkTooltipText('United Arab Emirates'), {
            timeout: 5000
          });
        });

        it('trigger tooltip from keyboard for truncated axis label', async () => {
          const items = gdpData.map((i, index) => ({
            id: index,
            value: i.gdp
          }));

          await setupBarChart({
            width: '1000px',
            height: '300px',
            series: [
              {
                id: 0,
                name: 'GDP',
                items
              }
            ],
            getDataItem: (_, gIndex) => items[gIndex],
            groups: gdpData.map((i) => ({
              id: i.country
            })),
            drilling: 'on'
          });

          const chart = screen.getByRole('application');
          expect(chart).not.null;
          await userEvent.tab();
          await userEvent.tab();
          await userEvent.keyboard('[ARROWRIGHT]');
          await waitFor(() => checkTooltipText('Saint Vincent and the Grenadines'), {
            timeout: 5000
          });
        });
      }
    }

    it('datatip renders on value = 0', async function () {
      await setupBarChart({
        groups: [{ id: 'Group A' }, { id: 'Group B' }],
        getDataItem: (seriesIndex: number, groupIndex: number) => {
          return seriesWithZeroData[seriesIndex].items[groupIndex];
        },
        series: seriesWithZeroData
      });

      const chart = screen.getByRole('application');
      const queryAllByDataObject = queryAllBy('data-oj-private-series-index');
      let marker = queryAllByDataObject(chart, '1')[0];
      await userEvent.hover(marker);
      await waitFor(() => {
        const targetElement = screen.queryByRole('tooltip') as HTMLElement;
        // eslint-disable-next-line
        expect(getByText(targetElement, 'Series 2')).to.be.not.null;
      });
      marker = queryAllByDataObject(chart, '2')[1];
      await userEvent.hover(marker);
      await waitFor(() => {
        const targetElement = screen.queryByRole('tooltip') as HTMLElement;
        // eslint-disable-next-line
        expect(getByText(targetElement, 'Series 3')).to.be.not.null;
      });
      marker = queryAllByDataObject(chart, '3')[0];
      await userEvent.hover(marker);
      await waitFor(() => {
        const targetElement = screen.queryByRole('tooltip') as HTMLElement;
        // eslint-disable-next-line
        expect(getByText(targetElement, 'Series 4')).to.be.not.null;
      });
    });

    it('datatip renders for reference area and line', async function () {
      const xAxisReferenceObjects: ReferenceObjectData[] = [
        {
          id: '0',
          low: 0,
          high: 0.3,
          type: 'area',
          accessibleLabel: 'Reference Area X=0 to X=0.3',
          location: 'front'
        }
      ];

      await setupBarChart({
        series,
        groups: [{ id: 'Group A' }, { id: 'Group B' }],
        getDataItem: (seriesIndex: number, groupIndex: number) => {
          return seriesWithZeroData[seriesIndex].items[groupIndex];
        },
        xAxis: {
          title: 'X Axis title',
          referenceObjects: xAxisReferenceObjects
        }
      });

      const chart = screen.getByRole('application');
      const queryAllByDataObject = queryAllBy('data-oj-private-ref-obj-id');
      const refArea = queryAllByDataObject(chart, '0')[0];
      await userEvent.hover(refArea);
      await waitFor(() => {
        const targetElement = screen.queryByRole('tooltip') as HTMLElement;
        // eslint-disable-next-line
        expect(getByText(targetElement, `${xAxisReferenceObjects[0].accessibleLabel}`)).to.be.not
          .null;
      });
    });

    it('drag mode buttons', async function () {
      await setupBarChart({
        dragMode: 'user',
        zoomAndScroll: 'live',
        selectionMode: 'multiple',
        width: '500px',
        height: '500px',
        series,
        groups: [{ id: 'Group1' }, { id: 'GroupB' }],
        getDataItem: (i: number, j: number) => series[i].items[j],
        'aria-label': 'ariaText'
      });

      const chart = screen.getByRole('application');
      expect(chart).not.null;
      const queryAllByGroupIndex = queryAllBy('data-oj-private-group-index');
      const firstBar = queryAllByGroupIndex(chart, '0')[0];
      await userEvent.hover(firstBar as Element);
      await waitFor(() => {
        const buttons = screen.getByRole('toolbar');
        expect(buttons).not.null;
        // eslint-disable-next-line
        expect(buttons.children.length).to.equal(2);
        if (clientHints.platform === 'mac' || clientHints.platform === 'windows') {
          expect(screen.getByText('Marquee zoom')).not.null;
          expect(screen.getByText('Marquee select')).not.null;
        } else if (clientHints.deviceType === 'phone') {
          expect(screen.getByText('Pan')).not.null;
          expect(screen.getByText('Marquee select')).not.null;
        }
      });
    });

    it('drag mode buttons when selection mode is multiple', async function () {
      await setupBarChart({
        selectionMode: 'multiple',
        width: '500px',
        height: '500px',
        series,
        groups: [{ id: 'Group1' }, { id: 'GroupB' }],
        getDataItem: (i: number, j: number) => series[i].items[j],
        'aria-label': 'ariaText'
      });

      const chart = screen.getByRole('application');
      expect(chart).not.null;
      const queryAllByGroupIndex = queryAllBy('data-oj-group-index');
      const firstBar = queryAllByGroupIndex(chart, '0')[0];
      await userEvent.hover(firstBar as Element);
      await waitFor(() => {
        if (clientHints.platform === 'mac' || clientHints.platform === 'windows') {
          expect(screen.queryByText('Marquee select')).to.be.null;
        } else if (clientHints.deviceType === 'phone') {
          const button = screen.getByRole('toolbar');
          expect(button).not.null;
          expect(button.children.length).to.equal(1);
          expect(screen.getByText('Marquee select')).not.null;
        }
      });
    });

    it('Renders custom datatip content, default border color, and default borderwidth', async function () {
      const datatip = () => {
        return {
          content: <RatingGauge value={3} max={5}></RatingGauge>
        };
      };

      await setupBarChart({
        width: '500px',
        height: '500px',
        datatip,
        getDataItem: (i: number, j: number) => series[i].items[j],
        series,
        groups: [{ id: 'Group1' }, { id: 'GroupB' }]
      });

      const chart = screen.getByRole('application');
      const queryAllByGroupIndex = queryAllBy('data-oj-private-series-index');
      const firstBar = queryAllByGroupIndex(chart, '0')[0];
      await userEvent.hover(firstBar as Element);

      await waitFor(() => {
        const datatipElements = screen.getAllByRole('tooltip');
        expect(datatipElements.length).equals(1);
        const datatip = datatipElements[0] as HTMLElement;
        checkRatingGaugeCustomContent(datatip);
      });
    });

    it('Renders default content, custom border color, and default borderwidth', async function () {
      const datatip = () => {
        return {
          borderColor: 'red'
        };
      };

      await setupBarChart({
        width: '500px',
        height: '500px',
        datatip,
        getDataItem: (i: number, j: number) => series[i].items[j],
        series,
        groups: [{ id: 'Group1' }, { id: 'GroupB' }]
      });

      const chart = screen.getByRole('application');
      const queryAllByGroupIndex = queryAllBy('data-oj-private-series-index');
      const firstBar = queryAllByGroupIndex(chart, '0')[0];
      await userEvent.hover(firstBar as Element);

      await waitFor(() => {
        const datatipElements = screen.getAllByRole('tooltip');
        expect(datatipElements.length).equals(1);
        const datatip = datatipElements[0] as HTMLElement;
        checkBorderColor(datatip, 'red');
      });
    });

    it('Renders default content, and disabled border', async function () {
      const datatip = () => {
        return {
          defaultContainer: 'disabled' as const
        };
      };

      await setupBarChart({
        width: '500px',
        height: '500px',
        datatip,
        getDataItem: (i: number, j: number) => series[i].items[j],
        series,
        groups: [{ id: 'Group1' }, { id: 'GroupB' }]
      });

      const chart = screen.getByRole('application');
      const queryAllByGroupIndex = queryAllBy('data-oj-private-series-index');
      const firstBar = queryAllByGroupIndex(chart, '0')[0];
      await userEvent.hover(firstBar as Element);

      await waitFor(() => {
        const datatipElements = screen.getAllByRole('tooltip');
        expect(datatipElements.length).equals(1);
        const datatip = datatipElements[0] as HTMLElement;
        checkNoBorder(datatip);
      });
    });

    it('renders initial zooming to first group when initialZooming="first"', async function () {
      const items = gdpData.map((i, index) => ({
        id: index,
        value: i.gdp
      }));
      await setupBarChart({
        initialZooming: 'first',
        zoomAndScroll: 'live',
        getDataItem: (_, gIndex) => items[gIndex],
        groups: gdpData.map((i) => ({
          id: i.country
        }))
      });
      const chart = screen.getByRole('application');
      const queryAllByGroupIndex = queryAllBy('data-oj-private-group-index');
      const firstIndex = 0;
      const lastIndex = gdpData.length - 1;
      // First datapoint visible, last datapoint not visible when initialZooming="first"
      expect(queryAllByGroupIndex(chart, String(firstIndex)).length).to.be.greaterThan(0);
      expect(queryAllByGroupIndex(chart, String(lastIndex)).length).to.equal(0);
    });

    it('renders initial zooming to last group when initialZooming="last"', async function () {
      const items = gdpData.map((i, index) => ({
        id: index,
        value: i.gdp
      }));
      await setupBarChart({
        initialZooming: 'last',
        zoomAndScroll: 'live',
        getDataItem: (_, gIndex) => items[gIndex],
        groups: gdpData.map((i) => ({
          id: i.country
        }))
      });
      const chart = screen.getByRole('application');
      const queryAllByGroupIndex = queryAllBy('data-oj-private-group-index');
      const firstIndex = 0;
      const lastIndex = gdpData.length - 1;
      // Last datapoint visible, first datapoint not visible when initialZooming="last"
      expect(queryAllByGroupIndex(chart, String(lastIndex)).length).to.be.greaterThan(0);
      expect(queryAllByGroupIndex(chart, String(firstIndex)).length).to.equal(0);
    });

    it('renders both first and last datapoints when initialZooming="none"', async function () {
      const items = gdpData.map((i, index) => ({
        id: index,
        value: i.gdp
      }));
      await setupBarChart({
        initialZooming: 'none',
        zoomAndScroll: 'live',
        getDataItem: (_, gIndex) => items[gIndex],
        groups: gdpData.map((i) => ({
          id: i.country
        }))
      });
      const chart = screen.getByRole('application');
      const queryAllByGroupIndex = queryAllBy('data-oj-private-group-index');
      const firstIndex = 0;
      const lastIndex = gdpData.length - 1;
      // Both first and last datapoints should be visible when initialZooming="none"
      expect(queryAllByGroupIndex(chart, String(firstIndex)).length).to.be.greaterThan(0);
      expect(queryAllByGroupIndex(chart, String(lastIndex)).length).to.be.greaterThan(0);
    });
  }

  it('should render reference area when referenceObjects type is area', async function () {
    const series = [
      {
        id: 0,
        name: 'Series 1',
        items: [
          { value: 42, id: 0, accessibleLabel: 'Series 1. Group A. Value 42' },
          { value: 34, id: 1, accessibleLabel: 'Series 1. Group B. Value 34' },
          { value: 64, id: 2, accessibleLabel: 'Series 1. Group C. Value 64' }
        ]
      }
    ];

    const groups = [{ id: 'Group A' }, { id: 'Group B' }, { id: 'Group C' }];

    const getDataItemUtil = (seriesIndex: number, groupIndex: number) =>
      series[seriesIndex].items[groupIndex];

    const xAxisReferenceObjects: ReferenceObjectData[] = [
      {
        id: '0',
        low: 0,
        high: 0.3,
        type: 'area',
        accessibleLabel: 'Reference Area X=0 to X=0.3',
        location: 'front'
      }
    ];

    await setupBarChart({
      series,
      groups,
      getDataItem: getDataItemUtil,
      xAxis: {
        title: 'X Axis title',
        referenceObjects: xAxisReferenceObjects
      }
    });

    const chart = screen.getByRole('application');
    const refArea = chart.querySelector(
      '[class*="ChartStyles_refArea"], rect[class*="reference"], path[class*="reference"]'
    );
    expect(refArea).not.null;
  });
  it('should render reference area when referenceObjects type is area', async function () {
    const series = [
      {
        id: 0,
        name: 'Series 1',
        items: [
          { value: 42, id: 0, accessibleLabel: 'Series 1. Group A. Value 42' },
          { value: 34, id: 1, accessibleLabel: 'Series 1. Group B. Value 34' },
          { value: 64, id: 2, accessibleLabel: 'Series 1. Group C. Value 64' }
        ]
      },
      {
        id: 1,
        name: 'Series 2',
        items: [
          { value: 55, id: 3, accessibleLabel: 'Series 2. Group A. Value 55' },
          { value: 45, id: 4, accessibleLabel: 'Series 2. Group B. Value 45' },
          { value: 30, id: 5, accessibleLabel: 'Series 2. Group C. Value 30' }
        ]
      },
      {
        id: 2,
        name: 'Series 3',
        items: [
          { value: 36, id: 6, accessibleLabel: 'Series 3. Group A. Value 36' },
          { value: 40, id: 7, accessibleLabel: 'Series 3. Group B. Value 40' },
          { value: 70, id: 8, accessibleLabel: 'Series 3. Group C. Value 70' }
        ]
      }
    ];

    const groups = [{ id: 'Group A' }, { id: 'Group B' }, { id: 'Group C' }];

    const getDataItemUtil = (seriesIndex: number, groupIndex: number) =>
      series[seriesIndex].items[groupIndex];

    const xAxisReferenceObjects: ReferenceObjectData[] = [
      {
        id: '1',
        value: 1,
        accessibleLabel: 'Constant Line X=1',
        location: 'front',
        type: 'line'
      }
    ];

    await setupBarChart({
      series,
      groups,
      getDataItem: getDataItemUtil,
      xAxis: {
        title: 'X Axis title',
        referenceObjects: xAxisReferenceObjects
      }
    });
    const chart = screen.getByRole('application');

    const refLinePath = chart.querySelector('path[class*="ChartStyles_refLine"]');
    expect(refLinePath).not.null;

    expect(refLinePath?.getAttribute('role')).equals('img');

    expect(refLinePath?.classList.contains('ChartStyles_refLine'));

    const pathData = refLinePath?.getAttribute('d');
    expect(pathData).not.null;
  });

  it('Renders data label', async function () {
    await setupBarChart({
      width: '500px',
      height: '500px',
      getDataItem: (i: number, j: number) => ({
        ...series[i].items[j],
        labelPosition: 'center',
        label: series[i].items[j].value.toString()
      }),
      series,
      groups: [{ id: 'Group1' }, { id: 'GroupB' }]
    });

    // TODO:JET-65258
    // workaround for test issue JET-65258
    // some dom are missing from chart if we do not add
    // this await userEvent.tab() and hence tests to check
    // dom will fail; maybe it's a delay issue.
    await userEvent.tab();
    await userEvent.tab();

    const label1 = screen.getByText('2');
    const label2 = screen.getByText('1');
    expect(label1).to.be.not.null;
    expect(label2).to.be.not.null;
  });

  if (typeof jest === 'undefined') {
    it('trigger context menu on mouse right click with correct context', async () => {
      await setupBarChart({
        contextMenuConfig: {
          itemsRenderer: () => {
            return (
              <>
                <MenuItem label="MenuItem1"></MenuItem>
                <MenuItem label="MenuItem2"></MenuItem>
                <MenuItem label="MenuItem3"></MenuItem>
              </>
            );
          }
        }
      });
      const chart = screen.getByRole('application');
      expect(chart).not.null;
      getTestContextMenuViaMouse(chart);
    });
    it('trigger context menu from Keyboard (shift + f10) with correct context', async () => {
      let menuContext, type;
      await setupBarChart({
        contextMenuConfig: {
          itemsRenderer: (context) => {
            menuContext = context;
            type = context.type;
            return (
              <>
                <MenuItem label="MenuItem1"></MenuItem>
                <MenuItem label="MenuItem2"></MenuItem>
                <MenuItem label="MenuItem3"></MenuItem>
              </>
            );
          }
        }
      });
      const chart = screen.getByRole('application');
      getTestContextMenuViaKeyboard(chart, menuContext, type);
    });

    it('should not trigger context menu when there is no data', async () => {
      await setupBarChart({
        series: [], // Empty series, no data
        groups: [],
        getDataItem: () => undefined,
        contextMenuConfig: {
          itemsRenderer: () => {
            return (
              <>
                <MenuItem label="MenuItem1"></MenuItem>
                <MenuItem label="MenuItem2"></MenuItem>
                <MenuItem label="MenuItem3"></MenuItem>
              </>
            );
          }
        }
      });
      const chart = screen.getByRole('application');
      getTestContextMenuForNoData(chart);
    });
    it('renders overview content', async () => {
      const { container } = await setupBarChart({
        overview: 'on',
        series: [
          {
            name: 'Series 1',
            id: 'Series 1',
            items: [
              {
                id: '1',
                value: 1
              },
              {
                id: '2',
                value: 2
              },
              {
                id: '3',
                value: 1
              },
              {
                id: '4',
                value: 2
              }
            ]
          }
        ],
        groups: [{ id: 'GroupA' }, { id: 'GroupB' }, { id: 'GroupC' }, { id: 'GroupD' }],
        xAxis: {
          viewportMax: 2,
          viewportMin: 1
        }
      });

      // eslint-disable-next-line
      const svgs = container.querySelectorAll('svg');
      expect(svgs.length).to.equal(6);

      // eslint-disable-next-line
      expect(svgs[0].parentElement?.getAttribute('tabIndex')).to.equal('0');
      // eslint-disable-next-line
      expect(svgs[1].parentElement?.getAttribute('tabIndex')).is.null;
      const chartStyles = getComputedStyle(svgs[0]);
      const overviewStyles = getComputedStyle(svgs[1]);
      expect(chartStyles.width).to.equal(overviewStyles.width);
      expect(parseInt(overviewStyles.height)).to.lessThanOrEqual(parseInt(chartStyles.height));

      const queryAllByDataObject = queryAllBy('data-oj-private-object');
      const chart = screen.getByRole('application');
      const window = queryAllByDataObject(chart, 'window');
      // eslint-disable-next-line
      const background = window[0].parentElement?.children[0];
      // @ts-ignore
      const windowWidth = (window[0] as SVGGElement).getBBox().width;
      // @ts-ignore
      const backgroundWidth = background.getBBox().width;
      expect(windowWidth / backgroundWidth).to.closeTo(0.25, 0.05);
    });
  }

  it('keyboard navigation in grouped stacked chart', async function () {
    const stackedSeries = [
      {
        name: 'A1',
        id: 'A1',
        items: [
          { id: 'a1g1', value: 10 },
          { id: 'a1g2', value: 20 }
        ]
      },
      {
        name: 'A2',
        id: 'A2',
        items: [
          { id: 'a2g1', value: 15 },
          { id: 'a2g2', value: 25 }
        ]
      },
      {
        name: 'B1',
        id: 'B1',
        items: [
          { id: 'b1g1', value: 12 },
          { id: 'b1g2', value: 22 }
        ]
      },
      {
        name: 'B2',
        id: 'B2',
        items: [
          { id: 'b2g1', value: 18 },
          { id: 'b2g2', value: 28 }
        ]
      }
    ];

    await setupBarChart({
      stacking: {
        getStackGroup: (s) => (String(s.id).startsWith('A') ? 'S1' : 'S2')
      },
      series: stackedSeries,
      groups: [{ id: 'GroupA' }, { id: 'GroupB' }],
      getDataItem: (i: number, j: number) => stackedSeries[i].items[j]
    });

    const chart = screen.getByRole('application');
    await userEvent.tab();

    const getActive = () => {
      const activeId = chart.getAttribute('aria-activedescendant')!;
      const active = queryById(chart, activeId!) as HTMLElement;
      return {
        seriesIndex: active.getAttribute('data-oj-private-series-index'),
        groupIndex: active.getAttribute('data-oj-private-group-index')
      };
    };

    // Initial focus should be first series of first group
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowRight]');
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('1');

    await userEvent.keyboard('[ArrowRight]');
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('1');

    await userEvent.keyboard('[ArrowLeft]');
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowLeft]');
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');
  });

  it('keyboard navigation in grouped stacked chart - up/down within stack', async function () {
    const stackedSeries = [
      {
        name: 'A1',
        id: 'A1',
        items: [
          { id: 'a1g1', value: 10 },
          { id: 'a1g2', value: 20 }
        ]
      },
      {
        name: 'A2',
        id: 'A2',
        items: [
          { id: 'a2g1', value: 15 },
          { id: 'a2g2', value: 25 }
        ]
      },
      {
        name: 'B1',
        id: 'B1',
        items: [
          { id: 'b1g1', value: 12 },
          { id: 'b1g2', value: 22 }
        ]
      },
      {
        name: 'B2',
        id: 'B2',
        items: [
          { id: 'b2g1', value: 18 },
          { id: 'b2g2', value: 28 }
        ]
      }
    ];

    await setupBarChart({
      stacking: {
        getStackGroup: (s: any) => (String(s.id).startsWith('A') ? 'S1' : 'S2')
      },
      series: stackedSeries as unknown as BarChartProps['series'],
      groups: [{ id: 'GroupA' }, { id: 'GroupB' }],
      getDataItem: (i: number, j: number) => stackedSeries[i].items[j]
    });

    const chart = screen.getByRole('application');
    await userEvent.tab();

    const getActive = () => {
      const activeId = chart.getAttribute('aria-activedescendant')!;
      const active = queryById(chart, activeId!) as HTMLElement;
      return {
        seriesIndex: active.getAttribute('data-oj-private-series-index'),
        groupIndex: active.getAttribute('data-oj-private-group-index')
      };
    };

    // Start at first series of first group (0,0).
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowUp]');
    expect(getActive().seriesIndex).to.equal('2');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowUp]');
    expect(getActive().seriesIndex).to.equal('1');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowDown]');
    expect(getActive().seriesIndex).to.equal('2');
    expect(getActive().groupIndex).to.equal('0');
  });

  it('keyboard navigation on stacked single group with negative value', async function () {
    const seriesNav = [
      {
        name: 'S1',
        id: 'S1',
        items: [
          { id: 's1a', value: 42 },
          { id: 's1b', value: 34 }
        ]
      },
      {
        name: 'S2',
        id: 'S2',
        items: [
          { id: 's2a', value: 55 },
          { id: 's2b', value: 30 }
        ]
      },
      {
        name: 'S3',
        id: 'S3',
        items: [
          { id: 's3a', value: 36 },
          { id: 's3b', value: -50 }
        ]
      }
    ];

    await setupBarChart({
      series: seriesNav as unknown as BarChartProps['series'],
      groups: [{ id: 'Group A' }, { id: 'Group B' }],
      getDataItem: (i: number, j: number) => seriesNav[i].items[j],
      stacking: 'on'
    });

    const chart = screen.getByRole('application');
    await userEvent.tab();

    const getActive = () => {
      const activeId = chart.getAttribute('aria-activedescendant')!;
      const active = queryById(chart, activeId!) as HTMLElement;
      return {
        seriesIndex: active.getAttribute('data-oj-private-series-index'),
        groupIndex: active.getAttribute('data-oj-private-group-index')
      };
    };

    // Start at 42 in Group A (seriesIndex 0, groupIndex 0)
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');

    // ArrowLeft should move to value 30 in Group B (seriesIndex 0`, groupIndex 1)
    await userEvent.keyboard('[ArrowRight]');
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('1');

    // ArrowDown should move to value -50 in Group B (seriesIndex 0, groupIndex 1)
    await userEvent.keyboard('[ArrowDown]');
    expect(getActive().seriesIndex).to.equal('2');
    expect(getActive().groupIndex).to.equal('1');
  });

  it('keyboard navigation in grouped stacked chart - RTL', async function () {
    const stackedSeries = [
      {
        name: 'A1',
        id: 'A1',
        items: [
          { id: 'a1g1', value: 10 },
          { id: 'a1g2', value: 20 }
        ]
      },
      {
        name: 'A2',
        id: 'A2',
        items: [
          { id: 'a2g1', value: 15 },
          { id: 'a2g2', value: 25 }
        ]
      },
      {
        name: 'B1',
        id: 'B1',
        items: [
          { id: 'b1g1', value: 12 },
          { id: 'b1g2', value: 22 }
        ]
      },
      {
        name: 'B2',
        id: 'B2',
        items: [
          { id: 'b2g1', value: 18 },
          { id: 'b2g2', value: 28 }
        ]
      }
    ];

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);
    const envRtl = {
      translations: { '@oracle/oraclejet-preact': translations },
      user: { direction: 'rtl' as const }
    };

    const barChart = (
      <BarChart
        width="500px"
        height="500px"
        stacking={{ getStackGroup: (s) => (String(s.id).startsWith('A') ? 'S1' : 'S2') }}
        series={stackedSeries}
        groups={[{ id: 'GroupA' }, { id: 'GroupB' }]}
        getDataItem={(i: number, j: number) => stackedSeries[i].items[j]}
      />
    );
    render(<RootEnvironmentProvider environment={envRtl}>{barChart}</RootEnvironmentProvider>);

    await waitFor(() => {
      const chart = screen.getByRole('application');
      expect(chart).to.be.not.null;
    });

    const chart = screen.getByRole('application');
    await userEvent.tab();

    const getActive = () => {
      const activeId = chart.getAttribute('aria-activedescendant')!;
      const active = queryById(chart, activeId!) as HTMLElement;
      return {
        seriesIndex: active.getAttribute('data-oj-private-series-index'),
        groupIndex: active.getAttribute('data-oj-private-group-index')
      };
    };

    // Start at first series of first group (0,0).
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowUp]');
    expect(getActive().seriesIndex).to.equal('2');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowUp]');
    expect(getActive().seriesIndex).to.equal('1');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowDown]');
    expect(getActive().seriesIndex).to.equal('2');
    expect(getActive().groupIndex).to.equal('0');
  });

  it('keyboard navigation in grouped stacked chart with negative values', async function () {
    const seriesNeg = [
      {
        name: 'A1',
        id: 'A1',
        items: [
          { id: 'a1g1', value: 10 },
          { id: 'a1g2', value: 20 }
        ]
      },
      {
        name: 'A2',
        id: 'A2',
        items: [
          { id: 'a2g1', value: 15 },
          { id: 'a2g2', value: 25 }
        ]
      },
      {
        name: 'B1',
        id: 'B1',
        items: [
          { id: 'b1g1', value: -12 },
          { id: 'b1g2', value: -22 }
        ]
      },
      {
        name: 'B2',
        id: 'B2',
        items: [
          { id: 'b2g1', value: -18 },
          { id: 'b2g2', value: -28 }
        ]
      }
    ];

    await setupBarChart({
      stacking: {
        getStackGroup: (s: any) => (String(s.id).startsWith('A') ? 'S1' : 'S2')
      },
      series: seriesNeg as unknown as BarChartProps['series'],
      groups: [{ id: 'GroupA' }, { id: 'GroupB' }],
      getDataItem: (i: number, j: number) => seriesNeg[i].items[j]
    });

    const chart = screen.getByRole('application');
    await userEvent.tab();

    const getActive = () => {
      const activeId = chart.getAttribute('aria-activedescendant')!;
      const active = queryById(chart, activeId!) as HTMLElement;
      return {
        seriesIndex: active.getAttribute('data-oj-private-series-index'),
        groupIndex: active.getAttribute('data-oj-private-group-index')
      };
    };

    // Initial active (0,0)
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');

    await userEvent.keyboard('[ArrowUp]');
    expect(getActive().seriesIndex).to.equal('1');
    expect(getActive().groupIndex).to.equal('0');

    // ArrowDown within negative stack (S2): move deeper within stack to series 3
    await userEvent.keyboard('[ArrowDown]');
    expect(getActive().seriesIndex).to.equal('0');
    expect(getActive().groupIndex).to.equal('0');

    // ArrowUp within negative stack: back to series 2
    await userEvent.keyboard('[ArrowDown]');
    expect(getActive().seriesIndex).to.equal('2');
    expect(getActive().groupIndex).to.equal('0');
  });
});

if (typeof jest === 'undefined') {
  describe('Drag and Drop', () => {
    let originalMouseDescriptors: ReturnType<typeof mockMouseEventPageXYOffsetXY>;
    beforeEach(() => {
      originalMouseDescriptors = mockMouseEventPageXYOffsetXY();
      setupUseTextDimensionsDependenciesMock(window);
    });
    afterEach(() => restoreMouseEventPageXYOffsetXY(originalMouseDescriptors));
    it('Drag item and Drop on y axis Accept', async () => {
      const callbacks = {
        startCalled: false,
        enterCalled: false,
        overCalled: false,
        dropCalled: false,
        dragDetailCorrect: false,
        dropDetailCorrect: false
      };
      const dragStartCallback: BarChartProps['onDragStart'] = (detail) => {
        if (detail?.type === 'items') {
          callbacks.dragDetailCorrect = true;
          expect(detail.items[0].seriesIndex === 0 && detail.items[0].groupIndex === 0).to.be.true;
        }
      };
      const dropCallback: (detail: any) => boolean = (detail) => {
        if (detail?.type === 'yAxis') {
          callbacks.dropDetailCorrect = true;
          expect(detail.y).to.not.null;
          return true;
        }
        return false;
      };
      const dndCallbacks = getDndCallbacks(callbacks, dragStartCallback, dropCallback);
      const itemsDraggable: BarChartProps['itemsDraggable'] = () => {
        return 'on';
      };
      await setupBarChart({
        ...dndCallbacks,
        itemsDraggable
      });

      const chart = screen.getByRole('application');
      await userEvent.tab();
      // eslint-disable-next-line
      const item = chart.querySelector(
        '[data-oj-private-series-index="0"][data-oj-private-group-index="0"]'
      ) as Element;
      expect(item.getAttribute('data-oj-private-series-index')).to.equal('0');
      const dropOptions = getEventCoords(20, 20);
      // eslint-disable-next-line
      const dropRect = chart.firstElementChild?.children[5] as Element;
      testDnd(item, chart, dropRect, callbacks, true, dropOptions);
    });

    it('Drag item and Drop on x axis Accept', async () => {
      const callbacks = {
        startCalled: false,
        enterCalled: false,
        overCalled: false,
        dropCalled: false,
        dragDetailCorrect: false,
        dropDetailCorrect: false
      };
      const dragStartCallback: BarChartProps['onDragStart'] = (detail) => {
        if (detail?.type === 'items') {
          callbacks.dragDetailCorrect = true;
          expect(detail.items[0].seriesIndex === 0 && detail.items[0].groupIndex === 0).to.be.true;
        }
      };

      const dropCallback: (detail: any) => boolean = (detail) => {
        if (detail?.type === 'xAxis') {
          callbacks.dropDetailCorrect = true;
          expect(detail.x).to.not.null;
          return true;
        }
        return false;
      };
      const dndCallbacks = getDndCallbacks(callbacks, dragStartCallback, dropCallback);
      const itemsDraggable: BarChartProps['itemsDraggable'] = () => {
        return 'on';
      };
      await setupBarChart({
        ...dndCallbacks,
        itemsDraggable
      });

      const chart = screen.getByRole('application');
      await userEvent.tab();
      // eslint-disable-next-line
      const item = chart.querySelector(
        '[data-oj-private-series-index="0"][data-oj-private-group-index="0"]'
      ) as Element;
      expect(item.getAttribute('data-oj-private-series-index')).to.equal('0');
      const dropOptions = getEventCoords(40, 480);
      // eslint-disable-next-line
      const dropRect = chart.firstElementChild?.children[7] as Element;
      testDnd(item, chart, dropRect, callbacks, true, dropOptions);
    });
    it('Drag group and Drop on plot area Accept', async () => {
      const callbacks = {
        startCalled: false,
        enterCalled: false,
        overCalled: false,
        dropCalled: false,
        dragDetailCorrect: false,
        dropDetailCorrect: false
      };
      const dragStartCallback: BarChartProps['onDragStart'] = (detail) => {
        if (detail?.type === 'xAxisTickLabel') {
          callbacks.dragDetailCorrect = true;
          expect(detail.groupIndex === 0).to.be.true;
        }
      };
      const dropCallback: (detail: any) => boolean = (detail) => {
        if (detail?.type === 'plotArea') {
          callbacks.dropDetailCorrect = true;
          expect(detail.x).to.not.null;
          expect(detail.y).to.not.null;
          return true;
        }
        return false;
      };
      const dndCallbacks = getDndCallbacks(callbacks, dragStartCallback, dropCallback);
      const groupDraggable: BarChartProps['groupDraggable'] = () => {
        return 'on';
      };
      await setupBarChart({
        ...dndCallbacks,
        groupDraggable
      });

      const chart = screen.getByRole('application');
      await userEvent.tab();
      // eslint-disable-next-line
      const group = chart.querySelector(
        '[data-oj-private-object="group"][data-oj-private-group-index="0"]'
      ) as Element;
      expect(group.getAttribute('data-oj-private-object')).to.equal('group');
      const dropOptions = getEventCoords(40, 40);
      // eslint-disable-next-line
      const dropRect = chart.firstElementChild?.children[1] as Element;
      testDnd(group, chart, dropRect, callbacks, true, dropOptions);
    });
    it('Drag item and Drop on y axis Reject', async () => {
      const callbacks = {
        startCalled: false,
        enterCalled: false,
        overCalled: false,
        dropCalled: false,
        dragDetailCorrect: false,
        dropDetailCorrect: false
      };
      const dragStartCallback: BarChartProps['onDragStart'] = (detail) => {
        if (detail?.type === 'items') {
          callbacks.dragDetailCorrect = true;
          expect(detail.items[0].seriesIndex === 0 && detail.items[0].groupIndex === 0).to.be.true;
        }
      };
      const dropCallback: (detail: any) => boolean = () => {
        return false;
      };
      const dndCallbacks = getDndCallbacks(callbacks, dragStartCallback, dropCallback);
      const itemsDraggable: BarChartProps['itemsDraggable'] = () => {
        return 'on';
      };
      await setupBarChart({
        ...dndCallbacks,
        itemsDraggable
      });

      const chart = screen.getByRole('application');
      await userEvent.tab();
      // eslint-disable-next-line
      const item = chart.querySelector(
        '[data-oj-private-series-index="0"][data-oj-private-group-index="0"]'
      ) as Element;
      expect(item.getAttribute('data-oj-private-series-index')).to.equal('0');
      const dropOptions = getEventCoords(20, 20);
      // eslint-disable-next-line
      const dropRect = chart.firstElementChild?.children[5] as Element;
      testDnd(item, chart, dropRect, callbacks, true, dropOptions, true);
    });

    it('Drag item and Drop on x axis Reject', async () => {
      const callbacks = {
        startCalled: false,
        enterCalled: false,
        overCalled: false,
        dropCalled: false,
        dragDetailCorrect: false,
        dropDetailCorrect: false
      };
      const dragStartCallback: BarChartProps['onDragStart'] = (detail) => {
        if (detail?.type === 'items') {
          callbacks.dragDetailCorrect = true;
          expect(detail.items[0].seriesIndex === 0 && detail.items[0].groupIndex === 0).to.be.true;
        }
      };

      const dropCallback: (detail: any) => boolean = () => {
        return false;
      };
      const dndCallbacks = getDndCallbacks(callbacks, dragStartCallback, dropCallback);
      const itemsDraggable: BarChartProps['itemsDraggable'] = () => {
        return 'on';
      };
      await setupBarChart({
        ...dndCallbacks,
        itemsDraggable
      });

      const chart = screen.getByRole('application');
      await userEvent.tab();
      // eslint-disable-next-line
      const item = chart.querySelector(
        '[data-oj-private-series-index="0"][data-oj-private-group-index="0"]'
      ) as Element;
      expect(item.getAttribute('data-oj-private-series-index')).to.equal('0');
      const dropOptions = getEventCoords(40, 480);
      // eslint-disable-next-line
      const dropRect = chart.firstElementChild?.children[7] as Element;
      testDnd(item, chart, dropRect, callbacks, true, dropOptions, true);
    });
    it('Drag group and Drop on plot area Reject', async () => {
      const callbacks = {
        startCalled: false,
        enterCalled: false,
        overCalled: false,
        dropCalled: false,
        dragDetailCorrect: false,
        dropDetailCorrect: false
      };
      const dragStartCallback: BarChartProps['onDragStart'] = (detail) => {
        if (detail?.type === 'xAxisTickLabel') {
          callbacks.dragDetailCorrect = true;
          expect(detail.groupIndex === 0).to.be.true;
        }
      };
      const dropCallback: (detail: any) => boolean = () => {
        return false;
      };
      const dndCallbacks = getDndCallbacks(callbacks, dragStartCallback, dropCallback);
      const groupDraggable: BarChartProps['groupDraggable'] = () => {
        return 'on';
      };
      await setupBarChart({
        ...dndCallbacks,
        groupDraggable
      });

      const chart = screen.getByRole('application');
      await userEvent.tab();
      // eslint-disable-next-line
      const group = chart.querySelector(
        '[data-oj-private-object="group"][data-oj-private-group-index="0"]'
      ) as Element;
      expect(group.getAttribute('data-oj-private-object')).to.equal('group');
      const dropOptions = getEventCoords(40, 40);
      // eslint-disable-next-line
      const dropRect = chart.firstElementChild?.children[1] as Element;
      testDnd(group, chart, dropRect, callbacks, true, dropOptions, true);
    });
  });
}
if (typeof jest === 'undefined') {
  describe('Preferred size ', () => {
    beforeEach(() => {
      setupUseTextDimensionsDependenciesMock(window);
    });
    it('Tests Y axis preferred size and bounds', async () => {
      const { chartRef } = await setupBarChart(
        {
          'aria-labelledby': 'chartAriaLabelledBy',
          'aria-label': 'chartAriaLabel',
          width: '800px',
          height: '300px'
        },
        { isPreferredSizeTest: true, returnRef: true }
      );
      const chart = screen.getByRole('application');
      expect(chart).not.null;
      expect(chartRef).not.null;

      const xBounds = chartRef!.current!._getXaxis().bounds;
      expect(xBounds?.height).to.be.closeTo(20, 1);
      expect(xBounds?.width).to.be.closeTo(759, 1);
      expect(xBounds?.x).to.be.closeTo(30, 1);
      expect(xBounds?.y).to.be.closeTo(272, 1);

      const yBounds = chartRef!.current!._getYaxis().bounds;
      expect(yBounds?.height).to.be.closeTo(258, 1);
      expect(yBounds?.width).to.be.closeTo(20, 1);
      expect(yBounds?.x).to.be.closeTo(10, 1);
      expect(yBounds?.y).to.be.closeTo(14, 1);
    });

    it('Tests X axis preferred size and bounds', async () => {
      const { chartRef } = await setupBarChart(
        {
          'aria-labelledby': 'chartAriaLabelledBy',
          'aria-label': 'chartAriaLabel',
          width: '800px',
          height: '300px'
        },
        { isPreferredSizeTest: true, returnRef: true }
      );
      const chart = screen.getByRole('application');
      expect(chart).not.null;
      expect(chartRef).not.null;

      const xBounds = chartRef!.current!._getXaxis().bounds;
      expect(xBounds?.height).to.be.closeTo(20, 1);
      expect(xBounds?.width).to.be.closeTo(759, 1);
      expect(xBounds?.x).to.be.closeTo(30, 1);
      expect(xBounds?.y).to.be.closeTo(272, 1);
    });
  });
}
